import { Box, Button, HStack, Stack, Text } from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiDownload, FiFile, FiFileText } from "react-icons/fi";
import { bffApi, type PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { useDocumentCategoryLabel } from "../../../../common/documents/useDocumentCategoryLabel";
import { useApiError } from "../../../../common/errors/useApiError";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { useAppDispatch } from "../../../../common/redux/hooks";
import { formatShortDate } from "../utils/displayFormatters";
import { SectionCard } from "./SectionCard";

export const EventDocumentsSection = ({ event }: { event: PortalSourcingEventDetailDto }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const displayer = useApiError();
  const documentCategoryLabel = useDocumentCategoryLabel();
  const [pendingId, setPendingId] = useState<string | null>(null);
  const documents = event.documents ?? [];
  const textDocuments = event.textDocuments ?? [];
  const locale = momentLocaleFor(i18n.language);
  if (documents.length === 0 && textDocuments.length === 0) return null;

  const handleDownload = async (documentId: string) => {
    setPendingId(documentId);
    try {
      const result = await dispatch(
        bffApi.endpoints.getPortalDocumentDownloadLink.initiate({
          eventId: event.id,
          documentId,
        })
      ).unwrap();
      window.open(result.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      displayer.trigger(error);
    } finally {
      setPendingId(null);
    }
  };

  return (
    <SectionCard icon={FiDownload} title={t("Procurement documents")} count={documents.length + textDocuments.length}>
      <Stack spacing="4">
        {documents.map((doc) => {
          const subtitleParts = [
            doc.category ? documentCategoryLabel(doc.category) : null,
            formatShortDate(doc.uploadedAt, locale),
          ].filter(Boolean);
          return (
            <HStack
              key={doc.id}
              borderWidth="1px"
              borderColor="smBorder"
              rounded="md"
              px="4"
              py="3"
              bg="smBackground"
              justify="space-between"
              align="center"
              spacing="3"
            >
              <HStack spacing="3" align="center" flex="1" minW="0">
                <Box color="smMuted" flexShrink="0">
                  <FiFile size={18} />
                </Box>
                <Stack spacing="0" minW="0">
                  <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                    {doc.name}
                  </Text>
                  <Text fontSize="xs" color="smMuted">
                    {subtitleParts.join(" · ")}
                  </Text>
                </Stack>
              </HStack>
              <Button
                size="sm"
                variant="outline"
                colorScheme="gray"
                leftIcon={<FiDownload />}
                onClick={() => handleDownload(doc.id)}
                isLoading={pendingId === doc.id}
                flexShrink="0"
              >
                {t("Download")}
              </Button>
            </HStack>
          );
        })}

        {textDocuments.map((doc) => (
          <Box key={doc.id} borderWidth="1px" borderColor="smBorder" rounded="md" px="4" py="3" bg="smBackground">
            <HStack spacing="3" align="center" mb="3">
              <Box color="smMuted" flexShrink="0">
                <FiFileText size={18} />
              </Box>
              <Stack spacing="0" minW="0">
                <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                  {doc.name || t("Untitled document")}
                </Text>
                <Text fontSize="xs" color="smMuted">
                  {formatShortDate(doc.createdAt, locale)}
                </Text>
              </Stack>
            </HStack>
            <TipTap editable={false} content={doc.text} hasBorder={false} padding="0" maxHeight="none" />
          </Box>
        ))}
      </Stack>
    </SectionCard>
  );
};
